import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import AICoachInsightCard from '../components/AICoachInsightCard';
import ConsistencyCard from '../components/ConsistencyCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Activity, Clock, Flame, Plus, Trash2, Loader2,
  TrendingUp, ChevronDown, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import GlowChartDefs, { GLOW_FILTER, glowTooltipStyle, glowTooltipItemStyle, glowTooltipLabelStyle, themeRgb } from '../components/charts/GlowChartDefs';
import StatTile from '../components/ui/StatTile';

const ACTIVITY_TYPES = [
  { id: 'running', labelFr: 'Course', labelEn: 'Running', icon: '🏃' },
  { id: 'cycling', labelFr: 'Velo', labelEn: 'Cycling', icon: '🚴' },
  { id: 'hiking', labelFr: 'Randonnee', labelEn: 'Hiking', icon: '🥾' },
  { id: 'swimming', labelFr: 'Natation', labelEn: 'Swimming', icon: '🏊' },
  { id: 'weight_training', labelFr: 'Musculation', labelEn: 'Weight Training', icon: '🏋️' },
  { id: 'yoga', labelFr: 'Yoga', labelEn: 'Yoga', icon: '🧘' },
  { id: 'hiit', labelFr: 'HIIT', labelEn: 'HIIT', icon: '⚡' },
  { id: 'team_sport', labelFr: 'Sport d\'equipe', labelEn: 'Team Sport', icon: '⚽' },
  { id: 'walking', labelFr: 'Marche', labelEn: 'Walking', icon: '🚶' },
  { id: 'other', labelFr: 'Autre', labelEn: 'Other', icon: '🎯' },
];

const INTENSITIES = [
  { id: 'light', labelFr: 'Leger', labelEn: 'Light', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { id: 'moderate', labelFr: 'Modere', labelEn: 'Moderate', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { id: 'intense', labelFr: 'Intense', labelEn: 'Intense', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

/**
 * Iter. 406.fr.31 — Strava-lite pace formatter.
 *
 * Returns a compact, locale-aware pace/speed string given an activity type,
 * duration in minutes and distance in km. The unit and convention depend on
 * what athletes actually expect to read:
 *   - running / walking / hiking → `m'ss"/km` (e.g. `4'37"/km`)
 *   - cycling                    → `xx.x km/h` (cyclists think in speed, not pace)
 *   - swimming                   → `m'ss"/100m`
 *
 * Returns `null` when the inputs are missing/invalid so the caller can simply
 * conditional-render without extra guards.
 */
const formatPace = (activityType, durationMin, distanceKm) => {
  const d = parseFloat(distanceKm);
  const m = parseFloat(durationMin);
  if (!d || d <= 0 || !m || m <= 0) return null;

  if (activityType === 'cycling') {
    const kmh = d / (m / 60);
    if (!isFinite(kmh) || kmh <= 0 || kmh > 100) return null;
    return `${kmh.toFixed(1)} km/h`;
  }

  if (activityType === 'swimming') {
    const secPer100 = (m * 60) / (d * 10);
    if (!isFinite(secPer100) || secPer100 <= 0) return null;
    const mm = Math.floor(secPer100 / 60);
    const ss = Math.round(secPer100 % 60).toString().padStart(2, '0');
    return `${mm}'${ss}"/100m`;
  }

  if (['running', 'walking', 'hiking'].includes(activityType)) {
    const secPerKm = (m * 60) / d;
    if (!isFinite(secPerKm) || secPerKm <= 0) return null;
    const mm = Math.floor(secPerKm / 60);
    const ss = Math.round(secPerKm % 60).toString().padStart(2, '0');
    return `${mm}'${ss}"/km`;
  }

  return null;
};

export default function Activities() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const fr = language === 'fr';

  const [showLog, setShowLog] = useState(false);
  const [logType, setLogType] = useState('running');
  const [logDuration, setLogDuration] = useState('30');
  const [logIntensity, setLogIntensity] = useState('moderate');
  // Iter. 406.fr.22 — Custom activity name (only when logType === 'other').
  // Lets users log Tennis, Pickleball, Escalade, Padel, etc. — anything
  // outside our 9 preset types. Capped at 60 chars on the backend.
  const [logCustomName, setLogCustomName] = useState('');
  // Iter. 406.fr.29 — Optional distance (km) + manual calories override.
  // Empty string = "let the backend auto-calculate". Both bounded server-side.
  const [logDistance, setLogDistance] = useState('');
  const [logCaloriesOverride, setLogCaloriesOverride] = useState('');
  // Iter. 406.s — Rattrapage : explicit date picker for catch-up logging.
  // Defaults to today; the modal exposes a date input so users can back-date.
  const todayStr = new Date().toISOString().slice(0, 10);
  const [logDate, setLogDate] = useState(todayStr);
  const [logging, setLogging] = useState(false);

  const [todayData, setTodayData] = useState({ activities: [], total_calories_burned: 0, total_minutes: 0, count: 0 });
  const [statsData, setStatsData] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  // Iter. 406.fr.23 — Historique multi-jours (au-delà d'« Aujourd'hui »).
  // Pagination par curseur : on fetch 30 lignes, l'utilisateur peut charger
  // les plus anciennes via le bouton « Voir plus ».
  const [historyItems, setHistoryItems] = useState([]);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async (reset = false) => {
    setHistoryLoading(true);
    try {
      const params = { limit: 30 };
      if (!reset && historyCursor) params.before = historyCursor;
      const { data } = await api.get('/activities/history', { params });
      setHistoryItems(prev => (reset ? data.activities : [...prev, ...data.activities]));
      setHistoryCursor(data.next_cursor);
      setHistoryHasMore(!!data.has_more);
    } catch { /* */ }
    finally { setHistoryLoading(false); }
  };

  const fetchData = async () => {
    try {
      const [todayRes, statsRes] = await Promise.all([
        api.get('/activities/today'),
        api.get(`/activities/stats?period=${statsPeriod}`),
      ]);
      setTodayData(todayRes.data);
      setStatsData(statsRes.data);
    } catch { /* */ }
    finally { setLoading(false); }
    // Reset & reload history alongside today/stats
    fetchHistory(true);
  };

  useEffect(() => { if (user) fetchData(); }, [user, statsPeriod]);

  // Assistant IA deep-link: /activities?action=log&type=running&duration=30
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const action = sp.get('action');
    const type = sp.get('type');
    const duration = sp.get('duration');
    if (!action && !type && !duration) return;
    if (action === 'log') {
      if (type) setLogType(type.slice(0, 30));
      if (duration) {
        const d = parseInt(duration, 10);
        if (!isNaN(d) && d > 0 && d < 600) setLogDuration(String(d));
      }
      setShowLog(true);
    }
    // Clean URL
    const next = new URLSearchParams(sp);
    ['action', 'type', 'duration'].forEach(k => next.delete(k));
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLog = async () => {
    const dur = parseInt(logDuration);
    if (!dur || dur <= 0) { toast.error(t('pages.activities_invalid_duration')); return; }
    // Iter. 406.fr.22 — When "Autre" is picked, require the custom name so
    // the row isn't logged as a generic "Autre" without context.
    if (logType === 'other' && !logCustomName.trim()) {
      toast.error(fr ? 'Précise ton activité (ex: Tennis, Padel...)' : 'Name your activity (e.g. Tennis, Padel...)');
      return;
    }
    setLogging(true);
    try {
      const payload = {
        activity_type: logType,
        duration_minutes: dur,
        intensity: logIntensity,
        // Iter. 406.s — Send the user-chosen date so the backend stores
        // `activity_at` and the row no longer clumps on "Today" by mistake.
        activity_date: logDate || todayStr,
      };
      if (logType === 'other' && logCustomName.trim()) {
        payload.custom_name = logCustomName.trim().slice(0, 60);
      }
      // Iter. 406.fr.29 — Optional distance (km) + manual calories override.
      const distRaw = (logDistance || '').toString().trim().replace(',', '.');
      if (distRaw !== '') {
        const dist = parseFloat(distRaw);
        if (!isNaN(dist) && dist > 0 && dist <= 500) {
          payload.distance_km = dist;
        }
      }
      const calRaw = (logCaloriesOverride || '').toString().trim();
      if (calRaw !== '') {
        const cal = parseInt(calRaw, 10);
        if (!isNaN(cal) && cal > 0 && cal <= 5000) {
          payload.calories_burned = cal;
        }
      }
      const res = await api.post('/activities/log', payload);
      toast.success(`${t('pages.activities_logged')}! ${res.data.message}`);
      setShowLog(false);
      setLogDuration('30');
      setLogCustomName('');
      setLogDistance('');
      setLogCaloriesOverride('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.error'));
    } finally { setLogging(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/activities/${id}`);
      fetchData();
    } catch { /* */ }
  };

  const selectedType = ACTIVITY_TYPES.find(t => t.id === logType) || ACTIVITY_TYPES[0];

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[80vh]"><Loader2 className="w-12 h-12 text-primary animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-4 pt-6 md:p-8 max-w-3xl mx-auto space-y-4" data-testid="activities-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground">
              {t('pages.activities_title')}
            </h1>
            <p className="text-sm text-muted-foreground">{fr ? 'Ton journal multi-sports' : 'Your multi-sport journal'}</p>
          </div>
          <Button onClick={() => setShowLog(!showLog)} className="bg-primary text-primary-foreground shrink-0 mt-1" data-testid="log-activity-btn">
            <Plus className="w-4 h-4 mr-1" /> {fr ? 'Loguer' : 'Log'}
          </Button>
        </div>

        {/* Iter. 406.fr.43 — Lecture Coach YUMIX (lazy, non-bloquant) */}
        <AICoachInsightCard endpoint="/activities/ai-summary" period="week" />

        {/* Today Summary — GLOW stat cards (Iter. 388) */}
        <Card className="bg-card border-border" data-testid="today-activity-summary">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div
                className="p-2.5 rounded-xl border border-red-500/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(248,113,113,0.10), rgba(127,29,29,0.04))',
                  boxShadow: '0 0 14px rgba(248,113,113,0.20), inset 0 0 0 1px rgba(248,113,113,0.10)',
                }}
              >
                <Flame
                  className="w-5 h-5 text-red-400 mx-auto mb-1"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(248,113,113,0.75))' }}
                />
                <p
                  className="font-heading text-2xl font-black text-red-300 tabular-nums"
                  style={{ textShadow: '0 0 12px rgba(248,113,113,0.55)' }}
                >
                  {todayData.total_calories_burned}
                </p>
                <p className="text-[10px] text-muted-foreground uppercase">cal {fr ? 'brûlées' : 'burned'}</p>
              </div>
              <div
                className="p-2.5 rounded-xl border border-blue-500/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(96,165,250,0.10), rgba(30,58,138,0.04))',
                  boxShadow: '0 0 14px rgba(96,165,250,0.20), inset 0 0 0 1px rgba(96,165,250,0.10)',
                }}
              >
                <Clock
                  className="w-5 h-5 text-blue-400 mx-auto mb-1"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(96,165,250,0.75))' }}
                />
                <p
                  className="font-heading text-2xl font-black text-blue-300 tabular-nums"
                  style={{ textShadow: '0 0 12px rgba(96,165,250,0.55)' }}
                >
                  {todayData.total_minutes}
                </p>
                <p className="text-[10px] text-muted-foreground uppercase">min</p>
              </div>
              <div
                className="p-2.5 rounded-xl border border-emerald-500/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(52,211,153,0.10), rgba(6,95,70,0.04))',
                  boxShadow: '0 0 14px rgba(52,211,153,0.20), inset 0 0 0 1px rgba(52,211,153,0.10)',
                }}
              >
                <Activity
                  className="w-5 h-5 text-emerald-400 mx-auto mb-1"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(52,211,153,0.75))' }}
                />
                <p
                  className="font-heading text-2xl font-black text-emerald-300 tabular-nums"
                  style={{ textShadow: '0 0 12px rgba(52,211,153,0.55)' }}
                >
                  {todayData.count}
                </p>
                <p className="text-[10px] text-muted-foreground uppercase">{fr ? 'séances' : 'sessions'}</p>
              </div>
            </div>
            <p className="text-[10px] text-center text-muted-foreground/60 mt-2 italic">
              {fr ? 'Les calories brûlées creusent ton déficit. Ton budget alimentaire reste verrouillé.' : 'Calories burned deepen your deficit. Your food budget stays locked.'}
            </p>
          </CardContent>
        </Card>

        {/* Log Form */}
        {showLog && (
          <Card className="border-primary/30 bg-primary/5" data-testid="log-activity-form">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-semibold">{fr ? "Type d'activité" : 'Activity type'}</p>
              <div className="grid grid-cols-5 gap-2">
                {ACTIVITY_TYPES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setLogType(t.id)}
                    className={`p-2 rounded-lg text-center transition-all border-2 ${
                      logType === t.id ? 'border-primary bg-primary/20' : 'border-transparent bg-secondary/50'
                    }`}
                    data-testid={`activity-type-${t.id}`}
                  >
                    <span className="text-lg">{t.icon}</span>
                    <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{fr ? t.labelFr : t.labelEn}</p>
                  </button>
                ))}
              </div>

              {/* Iter. 406.fr.22 — Free-text input revealed when "Autre" is
                  selected so the user can name the activity (Tennis, Padel,
                  Escalade, etc.). 60-char cap mirrors the backend Pydantic
                  trim. */}
              {logType === 'other' && (
                <div data-testid="activity-custom-name-block">
                  <p className="text-xs text-muted-foreground mb-1">
                    {fr ? 'Nom de l\'activité' : 'Activity name'}
                    <span className="text-muted-foreground/50 ml-1">
                      ({logCustomName.length}/60)
                    </span>
                  </p>
                  <Input
                    type="text"
                    value={logCustomName}
                    onChange={(e) => setLogCustomName(e.target.value.slice(0, 60))}
                    placeholder={fr ? 'Ex : Tennis, Pickleball, Escalade...' : 'E.g. Tennis, Pickleball, Climbing...'}
                    className="h-10"
                    maxLength={60}
                    autoFocus
                    data-testid="activity-custom-name-input"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">{fr ? 'Durée (min)' : 'Duration (min)'}</p>
                  <Input type="number" value={logDuration} onChange={e => setLogDuration(e.target.value)} className="h-10 text-center" min="1" data-testid="activity-duration-input" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">{fr ? 'Intensite' : 'Intensity'}</p>
                  <div className="flex gap-1">
                    {INTENSITIES.map(i => (
                      <button
                        key={i.id}
                        onClick={() => setLogIntensity(i.id)}
                        className={`flex-1 py-2 rounded text-[10px] font-bold uppercase border ${
                          logIntensity === i.id ? i.color : 'bg-secondary/50 text-muted-foreground border-transparent'
                        }`}
                        data-testid={`intensity-${i.id}`}
                      >
                        {fr ? i.labelFr : i.labelEn}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Iter. 406.fr.29 — Optional distance + calories override.
                  Both fields are optional. Distance shown for cardio types
                  (running, cycling, hiking, walking, swimming, other) — hidden
                  for yoga/HIIT/musculation/team sport where it's meaningless. */}
              <div className="flex gap-3">
                {['running', 'cycling', 'hiking', 'walking', 'swimming', 'other'].includes(logType) && (
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">
                      {fr ? 'Distance (km)' : 'Distance (km)'}
                      <span className="ml-1 text-[10px] text-muted-foreground/70">{fr ? '— optionnel' : '— optional'}</span>
                    </p>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="500"
                      value={logDistance}
                      onChange={e => setLogDistance(e.target.value)}
                      placeholder={fr ? 'ex: 5.2' : 'e.g. 5.2'}
                      className="h-10 text-center"
                      data-testid="activity-distance-input"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">
                    {fr ? 'Calories (cal)' : 'Calories (cal)'}
                    <span className="ml-1 text-[10px] text-muted-foreground/70">{fr ? '— sinon estimé' : '— else auto'}</span>
                  </p>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="5000"
                    value={logCaloriesOverride}
                    onChange={e => setLogCaloriesOverride(e.target.value)}
                    placeholder={fr ? 'auto' : 'auto'}
                    className="h-10 text-center"
                    data-testid="activity-calories-override-input"
                  />
                </div>
              </div>

              {/* Iter. 406.s — Rattrapage date picker. Defaults to today,
                  capped at today to prevent future logs. */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {fr ? 'Date (rattrapage si nécessaire)' : 'Date (catch-up if needed)'}
                </p>
                <Input
                  type="date"
                  value={logDate}
                  max={todayStr}
                  onChange={(e) => setLogDate(e.target.value || todayStr)}
                  className="h-10"
                  data-testid="activity-date-input"
                />
                {logDate !== todayStr && (
                  <p className="text-[10px] text-amber-300 mt-1" data-testid="activity-date-rattrapage-note">
                    {fr
                      ? `Rattrapage actif : enregistré pour le ${logDate}.`
                      : `Catch-up active: saving for ${logDate}.`}
                  </p>
                )}
              </div>

              <Button onClick={handleLog} disabled={logging} className="w-full h-10 bg-primary text-primary-foreground" data-testid="submit-activity-btn">
                {logging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {fr ? `Loguer ${selectedType.icon} ${logDuration} min` : `Log ${selectedType.icon} ${logDuration} min`}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Today's Activities List */}
        {todayData.activities.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading uppercase">{fr ? 'Aujourd\'hui' : 'Today'}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {todayData.activities.map(a => {
                const at = ACTIVITY_TYPES.find(t => t.id === a.activity_type) || ACTIVITY_TYPES[9];
                // Iter. 406.fr.22 — When the user typed a custom name for an
                // "Autre" entry, display that instead of the generic label.
                const displayLabel = (a.activity_type === 'other' && a.custom_name)
                  ? a.custom_name
                  : (fr ? at.labelFr : at.labelEn);
                // Iter. 406.fr.31 — Strava-lite pace badge.
                const pace = formatPace(a.activity_type, a.duration_minutes, a.distance_km);
                return (
                  <div key={a.activity_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/40" data-testid={`activity-row-${a.activity_id}`}>
                    <span className="text-lg">{at.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{displayLabel}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {a.duration_minutes}min • {a.intensity}
                        {a.distance_km ? ` • ${a.distance_km} km` : ''}
                      </p>
                    </div>
                    {pace && (
                      <span
                        className="text-[10px] font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-1.5 py-0.5 rounded-md tabular-nums"
                        title={fr ? 'Allure / Vitesse' : 'Pace / Speed'}
                        data-testid={`activity-pace-${a.activity_id}`}
                      >
                        {pace}
                      </span>
                    )}
                    <span className="text-xs text-red-400 font-bold">{a.calories_burned} cal</span>
                    <button onClick={() => handleDelete(a.activity_id)} className="p-1 text-muted-foreground/40 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Iter. 406.fr.23 — Historique multi-jours.
            Affiche les activités passées (hors aujourd'hui) groupées par
            date, avec pagination par curseur via le bouton « Voir plus ». */}
        {historyItems.length > 0 && (
          <Card className="bg-card border-border" data-testid="activity-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading uppercase flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                {fr ? 'Historique' : 'History'}
                <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                  ({historyItems.length}{historyHasMore ? '+' : ''})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(() => {
                // Group by local YYYY-MM-DD
                const groups = {};
                for (const a of historyItems) {
                  const iso = a.activity_at || a.created_at;
                  const day = (iso || '').slice(0, 10);
                  if (!groups[day]) groups[day] = [];
                  groups[day].push(a);
                }
                const sortedDays = Object.keys(groups).sort((x, y) => (x < y ? 1 : -1));
                const fmtDate = (d) => {
                  try {
                    const dt = new Date(d + 'T12:00:00');
                    return dt.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', {
                      weekday: 'short', day: 'numeric', month: 'short',
                    });
                  } catch { return d; }
                };
                return sortedDays.map(day => (
                  <div key={day} data-testid={`history-day-${day}`}>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-bold mt-1 mb-1.5">
                      {fmtDate(day)}
                    </p>
                    {groups[day].map(a => {
                      const at = ACTIVITY_TYPES.find(t => t.id === a.activity_type) || ACTIVITY_TYPES[9];
                      const displayLabel = (a.activity_type === 'other' && a.custom_name)
                        ? a.custom_name
                        : (fr ? at.labelFr : at.labelEn);
                      const pace = formatPace(a.activity_type, a.duration_minutes, a.distance_km);
                      return (
                        <div
                          key={a.activity_id}
                          className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30 mb-1"
                          data-testid={`history-row-${a.activity_id}`}
                        >
                          <span className="text-lg">{at.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">{displayLabel}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {a.duration_minutes}min • {a.intensity}
                              {a.distance_km ? ` • ${a.distance_km} km` : ''}
                            </p>
                          </div>
                          {pace && (
                            <span
                              className="text-[10px] font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-1.5 py-0.5 rounded-md tabular-nums"
                              title={fr ? 'Allure / Vitesse' : 'Pace / Speed'}
                              data-testid={`history-pace-${a.activity_id}`}
                            >
                              {pace}
                            </span>
                          )}
                          <span className="text-xs text-red-400 font-bold">{a.calories_burned} cal</span>
                          <button
                            onClick={() => handleDelete(a.activity_id)}
                            className="p-1 text-muted-foreground/40 hover:text-destructive"
                            data-testid={`history-delete-${a.activity_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}

              {historyHasMore && (
                <button
                  onClick={() => fetchHistory(false)}
                  disabled={historyLoading}
                  className="w-full mt-2 py-2 rounded-lg border border-border bg-secondary/40 hover:bg-secondary/60 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="history-load-more-btn"
                >
                  {historyLoading
                    ? (fr ? 'Chargement…' : 'Loading…')
                    : (fr ? 'Voir plus' : 'Load more')}
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Iter. 406.fr.47 — Training Consistency Card (weekly streak) */}
        {statsData?.consistency && statsData.consistency.weeks_tracked > 0 && (
          <ConsistencyCard
            consistency={statsData.consistency}
            theme="orange"
            title={fr ? 'Consistance d\'Entraînement' : 'Training Consistency'}
            unitLabel={
              fr
                ? `semaines ≥${statsData.consistency.target_per_week} séances`
                : `weeks ≥${statsData.consistency.target_per_week} sessions`
            }
            streakLabel={fr ? 'Série actuelle' : 'Current streak'}
            bestLabel={fr ? 'Meilleure série' : 'Best streak'}
            onTargetLabel={
              fr
                ? `Cible : ${statsData.consistency.target_per_week} séances/semaine.`
                : `Target: ${statsData.consistency.target_per_week} sessions/week.`
            }
            streakUnit="w"
            fr={fr}
            testId="activities-consistency-card"
          />
        )}

        {/* Stats Chart */}
        <Card className="bg-card border-border" data-testid="activity-stats-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading uppercase flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {fr ? 'Volume d\'entraînement' : 'Training Volume'}
              </CardTitle>
              <div className="flex gap-1 bg-secondary/60 p-0.5 rounded-lg" data-testid="stats-period-toggle">
                {['week', 'month', 'year'].map(p => (
                  <button
                    key={p}
                    onClick={() => setStatsPeriod(p)}
                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                      statsPeriod === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                    }`}
                    data-testid={`period-${p}`}
                  >
                    {p === 'week' ? (fr ? 'Sem.' : 'Week') : p === 'month' ? (fr ? 'Mois' : 'Month') : (fr ? 'Année' : 'Year')}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {statsData?.daily_breakdown?.length > 0 ? (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statsData.daily_breakdown}>
                    <defs>
                      <GlowChartDefs id="activitiesBar" theme="orange" />
                    </defs>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
                      interval="preserveStartEnd"
                      minTickGap={statsPeriod === 'year' ? 20 : 12}
                      tickFormatter={(d) => {
                        if (!d) return '';
                        // Year view: backend returns "YYYY-MM" → show short month name
                        if (statsPeriod === 'year' && d.length === 7) {
                          const [y, m] = d.split('-').map(Number);
                          return new Date(y, m - 1, 1).toLocaleDateString(fr ? 'fr-FR' : 'en-US', { month: 'short' });
                        }
                        return d.slice(5);
                      }}
                    />
                    <YAxis tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} width={30} />
                    <Tooltip
                      cursor={{ fill: `rgba(${themeRgb('orange')},0.08)` }}
                      contentStyle={glowTooltipStyle(themeRgb('orange'))}
                      itemStyle={glowTooltipItemStyle()}
                      labelStyle={glowTooltipLabelStyle(themeRgb('orange'))}
                    />
                    <Bar
                      dataKey="minutes"
                      fill="url(#activitiesBar-gradient)"
                      radius={[6, 6, 0, 0]}
                      name={fr ? 'Minutes' : 'Minutes'}
                      style={{ filter: GLOW_FILTER(themeRgb('orange')) }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center text-muted-foreground/50 py-8 text-sm">{fr ? 'Aucune donnée pour cette période' : 'No data for this period'}</p>
            )}
            {statsData && statsData.total_sessions > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                <StatTile theme="orange"  value={statsData.total_sessions}           label={fr ? 'séances' : 'sessions'} compact />
                <StatTile theme="blue"    value={statsData.total_minutes}            label="min"                         compact />
                <StatTile theme="red"     value={statsData.total_calories_burned}    label="cal"                         compact />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
